import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getWorkspaceTheme } from '../utils/workspaceTheme';
import { DashboardOutlined, AnalyticsOutlined, DirectionsCarOutlined, PeopleOutline, MapOutlined, Timeline, ShieldOutlined, ReceiptLongOutlined, SettingsOutlined, BuildOutlined, SpeedOutlined, RouteOutlined, SensorsOutlined, AssignmentOutlined } from '@mui/icons-material';

const icons = {
  dashboard: DashboardOutlined, analytics: AnalyticsOutlined, vehicles: DirectionsCarOutlined,
  people: PeopleOutline, map: MapOutlined, trips: Timeline, geofencing: ShieldOutlined,
  invoices: ReceiptLongOutlined, settings: SettingsOutlined, maintenance: BuildOutlined,
  speed: SpeedOutlined, mileage: RouteOutlined, telemetry: SensorsOutlined, requests: AssignmentOutlined,
};

interface PageHeaderProps {
  title: string;
  description: React.ReactNode;
  icon?: keyof typeof icons;
  actions?: React.ReactNode;
}

/** A consistent section heading with room for each page's existing controls. */
const PageHeader: React.FC<PageHeaderProps> = ({ title, description, icon = 'dashboard', actions }) => {
  const Icon = icons[icon];
  const colors = getWorkspaceTheme(useTheme());
  return (
    <Box component="header" sx={{
      mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3, color: '#fff',
      background: colors.banner,
      boxShadow: colors.bannerShadow,
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 2, sm: 3 },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 2 }, flex: '1 1 220px', minWidth: 0 }}>
        <Icon aria-hidden sx={{ fontSize: { xs: 30, sm: 40 }, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" variant="h4" sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' }, lineHeight: 1.2, overflowWrap: 'anywhere' }}>{title}</Typography>
          <Typography component="div" variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.75, lineHeight: 1.6 }}>{description}</Typography>
        </Box>
      </Box>
      {actions && <Box sx={{
        maxWidth: '100%', width: { xs: '100%', md: 'auto' }, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
        '& > .MuiStack-root': { flexWrap: 'wrap', gap: 1, minWidth: 0, maxWidth: '100%', '& > *': { margin: '0 !important' } },
        '& .MuiButton-contained': { bgcolor: '#fff', color: colors.accent, '&:hover': { bgcolor: alpha('#fff', 0.9) } },
        '& .MuiButton-outlined': { color: '#fff', borderColor: 'rgba(255,255,255,0.6)' },
        '& .MuiIconButton-root': { color: '#fff', bgcolor: 'rgba(255,255,255,0.18)' },
        '& .Mui-disabled': { opacity: 0.5 },
        '& .MuiInputLabel-root, & .MuiSelect-icon, & .MuiInputBase-root': { color: '#fff !important' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5) !important' },
      }}>{actions}</Box>}
    </Box>
  );
};

export default PageHeader;
